// src/pages/app/DashboardBotHealthPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchDashboardBotHealth,
  DashboardBotHealthResponse
} from "../../api/dashboard";

const DashboardBotHealthPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("it")
    ? "it-IT"
    : i18n.resolvedLanguage?.startsWith("es")
    ? "es-ES"
    : "en-GB";

  const [data, setData] = useState<DashboardBotHealthResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchDashboardBotHealth()
      .then((res) => {
        if (mounted) setData(res);
      })
      .catch((err: any) => {
        console.error("Failed to load bot health", err);
        if (mounted) setError(err?.message || "Failed to load bot health.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    return [...data.items].sort((a, b) => b.healthScore - a.healthScore);
  }, [data]);

  return (
    <div className="page-container dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {t("dashboard.health.title", "Bot health")}
          </h1>
          <p className="page-subtitle">
            {t(
              "dashboard.health.subtitle",
              "Quick overview of bot readiness, channels, and recent activity."
            )}
          </p>
        </div>

        <Link to="/app/dashboard" className="btn-secondary">
          {t("dashboard.health.back", "Back to dashboard")}
        </Link>
      </div>

      {loading && (
        <div className="card">
          <p>{t("dashboard.loading", "Loading…")}</p>
        </div>
      )}

      {!loading && error && (
        <div className="card card-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="dashboard-health-grid">
          {sortedItems.length === 0 ? (
            <div className="card">
              <p className="card-empty">
                {t("dashboard.health.empty", "No bots found yet.")}
              </p>
            </div>
          ) : (
            sortedItems.map((bot) => (
              <div key={bot.botId} className="card dashboard-health-card">
                <div className="dashboard-health-header">
                  <div>
                    <h2 className="dashboard-health-title">{bot.botName}</h2>
                    <p className="dashboard-health-subtitle">
                      {t("dashboard.health.status", "Status")}:{" "}
                      <strong>{bot.status}</strong>
                    </p>
                  </div>
                  <div
                    className={
                      "dashboard-health-score" +
                      (bot.healthScore >= 75
                        ? " dashboard-health-score--good"
                        : bot.healthScore >= 50
                        ? " dashboard-health-score--ok"
                        : " dashboard-health-score--poor")
                    }
                  >
                    {bot.healthScore}
                  </div>
                </div>

                <div className="dashboard-health-meta">
                  <span>
                    {t("dashboard.health.lastConversation", "Last conversation")}:{" "}
                    {bot.lastConversationAt
                      ? new Date(bot.lastConversationAt).toLocaleString(locale)
                      : "—"}
                  </span>
                  <span>
                    {t("dashboard.health.conversationsLast30", "Conversations (30d)")}{" "}
                    <strong>{bot.conversationsLast30Days}</strong>
                  </span>
                </div>

                <div className="dashboard-health-badges">
                  <span className={bot.channels.web ? "badge-ok" : "badge"}>
                    Web
                  </span>
                  <span className={bot.channels.whatsapp ? "badge-ok" : "badge"}>
                    WhatsApp
                  </span>
                  <span className={bot.channels.facebook ? "badge-ok" : "badge"}>
                    Facebook
                  </span>
                  <span className={bot.channels.instagram ? "badge-ok" : "badge"}>
                    Instagram
                  </span>
                </div>

                <div className="dashboard-health-flags">
                  <span>
                    {t("dashboard.health.knowledge", "Knowledge")}:{" "}
                    {bot.knowledgeEnabled ? "On" : "Off"}
                  </span>
                  <span>
                    {t("dashboard.health.bookings", "Bookings")}:{" "}
                    {bot.calendarEnabled ? "On" : "Off"}
                  </span>
                </div>

                <div className="dashboard-health-actions">
                  <Link
                    to={`/app/bots/${bot.botId}`}
                    className="btn-secondary btn-xs"
                  >
                    {t("dashboard.health.viewBot", "View bot")}
                  </Link>
                  <Link
                    to={`/app/bots/${bot.botId}/settings`}
                    className="btn-secondary btn-xs"
                  >
                    {t("dashboard.health.settings", "Settings")}
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardBotHealthPage;
